import { useClient } from '@cortex/react-query';
import type { MCPConnection, ModelConfig, OrgChatConfig } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';

import { AgentMemoryAndSkillsSection } from '../../components/agents/AgentMemoryAndSkillsSection';
import { BackButton } from '../../components/common/BackButton';
import { PageHeader } from '../../components/common/PageHeader';
import { Spinner } from '../../components/common/Spinner';
import { routes } from '../../lib/constants';

// The global managed Formant assistant agent row (seeded in catalog-agents.ts).
// Its resolved memory/skills come from the same shared resolver as any agent.
const formantChatAgentId = 'agt_formant_chat';

export const Route = createFileRoute('/agents/assistant')({ component: FormantAssistantPage });

type ThinkingChoice = 'default' | 'on' | 'off';

const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };
const columnsStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))',
  gap: 'var(--sp-4)',
  alignItems: 'start',
};

function thinkingToChoice(v: boolean | null | undefined): ThinkingChoice {
  return v == null ? 'default' : v ? 'on' : 'off';
}

// The Formant assistant is a global, `managed` agent row (`agt_formant_chat`) —
// one tenant-agnostic base shared by every org, editable only by Formant. This
// page is the per-org *overlay* on that base: it layers instructions, model,
// tool scope, and thinking on top of the managed agent without forking it. It
// lives under /agents (not a separate Admin page) because it is that agent's
// org-level config surface — and mirrors the orchestration-agent editor's
// card-column layout so the two edit experiences feel like one product.
function FormantAssistantPage() {
  const client = useClient();
  const qc = useQueryClient();

  const config = useQuery({
    queryKey: ['org-chat-config'],
    queryFn: () => client.http.get('v1/org-chat-config').json<OrgChatConfig>(),
  });
  const models = useQuery({
    queryKey: ['model-configs'],
    queryFn: () =>
      client.http.get('v1/model-configs').json<{ items: ModelConfig[]; total: number }>(),
  });
  const connections = useQuery({
    queryKey: ['mcp-connections'],
    queryFn: () =>
      client.http.get('v1/mcp-connections').json<{ items: MCPConnection[]; total: number }>(),
  });

  const [promptAppend, setPromptAppend] = useState('');
  const [modelConfigId, setModelConfigId] = useState('');
  // Connections the org has turned OFF. Tools are on by default (empty = all
  // allowed); a ticked box means "let the assistant use this", so unticking
  // adds the connection here.
  const [disabledMcp, setDisabledMcp] = useState<string[]>([]);
  const [toolDenylist, setToolDenylist] = useState('');
  const [thinking, setThinking] = useState<ThinkingChoice>('default');

  useEffect(() => {
    if (config.data) {
      setPromptAppend(config.data.systemPromptAppend ?? '');
      setModelConfigId(config.data.modelConfigId ?? '');
      setDisabledMcp(config.data.disabledMcpConnectionIds ?? []);
      setToolDenylist((config.data.toolDenylist ?? []).join('\n'));
      setThinking(thinkingToChoice(config.data.defaultThinking));
    }
  }, [config.data]);

  const denylistArray = useMemo(
    () =>
      toolDenylist
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean),
    [toolDenylist],
  );

  // Dirty check — mirrors the orchestration editor's Save/Saved affordance so
  // the button reflects whether there's anything to persist.
  const isDirty = useMemo(() => {
    const base = config.data;
    if (!base) return false;
    const sameSet = (a: string[], b: string[]) =>
      a.length === b.length && [...a].sort().join('\0') === [...b].sort().join('\0');
    return (
      (promptAppend.trim() ? promptAppend : '') !== (base.systemPromptAppend ?? '') ||
      (modelConfigId || '') !== (base.modelConfigId ?? '') ||
      thinking !== thinkingToChoice(base.defaultThinking) ||
      !sameSet(disabledMcp, base.disabledMcpConnectionIds ?? []) ||
      !sameSet(denylistArray, base.toolDenylist ?? [])
    );
  }, [config.data, promptAppend, modelConfigId, thinking, disabledMcp, denylistArray]);

  const save = useMutation({
    mutationFn: () =>
      client.http
        .put('v1/org-chat-config', {
          json: {
            systemPromptAppend: promptAppend.trim() ? promptAppend : null,
            modelConfigId: modelConfigId || null,
            disabledMcpConnectionIds: disabledMcp,
            toolDenylist: denylistArray,
            defaultThinking: thinking === 'default' ? null : thinking === 'on',
          },
        })
        .json<OrgChatConfig>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-chat-config'] }),
  });

  if (config.isLoading) return <Spinner />;

  // Checkbox reflects "can use" (enabled); toggling off adds the connection to
  // the disabled set, toggling on removes it.
  const toggleMcp = (id: string) =>
    setDisabledMcp((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <>
      <PageHeader
        title="Formant AI Assistant"
        actions={
          <>
            <BackButton to={routes.AGENTS} label="Agents" />
            <button
              type="submit"
              form="formant-assistant-form"
              className="btn btn--primary btn--sm"
              disabled={save.isPending || !isDirty}
              title={!isDirty ? 'No unsaved changes' : undefined}
            >
              {save.isPending ? 'Saving…' : isDirty ? 'Save changes' : 'Saved'}
            </button>
          </>
        }
      />
      <p className="t-sm t-muted" style={{ maxWidth: 760, marginBottom: 'var(--sp-4)' }}>
        Formant AI is your organization&apos;s built-in assistant. It works out of the box — the
        settings here let you tailor it to your team: add your own guidance, choose the model it
        uses, and control which tools it can reach. Changes take effect the next time someone starts
        a chat.
      </p>
      {save.isError && (
        <div className="alert alert--danger" style={{ marginBottom: 'var(--sp-4)' }}>
          {(save.error as Error).message}
        </div>
      )}

      <form
        id="formant-assistant-form"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div style={columnsStyle}>
          <div className="card">
            <div className="card-header">
              <span className="t-mono-label">ASSISTANT · BEHAVIOR</span>
              <h3 className="t-h3">Behavior</h3>
            </div>
            <div className="card-body" style={stack3}>
              <label className="field">
                <span className="field-label">Custom instructions</span>
                <textarea
                  className="textarea"
                  rows={8}
                  value={promptAppend}
                  placeholder="Guidance for your team — e.g. 'Answer in metric units. Our fleet is the East Yard survey drones.' Leave blank to use the assistant as-is."
                  onChange={(e) => setPromptAppend(e.target.value)}
                />
                <span className="field-help">
                  Extra guidance for your organization, added on top of the assistant&apos;s
                  standard instructions. Its built-in behavior and safety limits still apply.
                </span>
              </label>

              <label className="field">
                <span className="field-label">Chat model</span>
                <select
                  className="select-native"
                  value={modelConfigId}
                  onChange={(e) => setModelConfigId(e.target.value)}
                >
                  <option value="">Default (managed Formant chat model)</option>
                  {(models.data?.items ?? []).map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
                <span className="field-help">
                  The model this assistant uses for your organization. Leave on default to use the
                  standard Formant model.
                </span>
              </label>

              <label className="field">
                <span className="field-label">Extended thinking</span>
                <select
                  className="select-native"
                  value={thinking}
                  onChange={(e) => setThinking(e.target.value as ThinkingChoice)}
                >
                  <option value="default">Model default</option>
                  <option value="on">On by default</option>
                  <option value="off">Off by default</option>
                </select>
                <span className="field-help">
                  Whether the assistant reasons with extended thinking before replying.
                </span>
              </label>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="t-mono-label">ASSISTANT · TOOLS</span>
              <h3 className="t-h3">Tools this assistant can use</h3>
            </div>
            <div className="card-body" style={stack3}>
              <div className="field">
                <span className="field-label">Tool connections</span>
                <span className="field-help" style={{ marginBottom: 'var(--sp-2)' }}>
                  The assistant can use every MCP connection your org has. Untick any you want to
                  keep it from using.
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-1)' }}>
                  {(connections.data?.items ?? []).map((c) => (
                    <label
                      key={c.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--sp-2)',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        className="checkbox"
                        checked={!disabledMcp.includes(c.id)}
                        onChange={() => toggleMcp(c.id)}
                      />
                      <span className="t-sm">{c.name}</span>
                    </label>
                  ))}
                  {(connections.data?.items ?? []).length === 0 && (
                    <span className="t-xs t-muted">No MCP connections yet.</span>
                  )}
                </div>
              </div>

              <label className="field">
                <span className="field-label">Blocked tools</span>
                <textarea
                  className="textarea"
                  rows={3}
                  value={toolDenylist}
                  placeholder="One tool name per line (e.g. issue_command). These exact tool names are withheld from the assistant's resolved tool set."
                  onChange={(e) => setToolDenylist(e.target.value)}
                />
                <span className="field-help">
                  Block a specific tool by name — finer-grained than turning off a whole connection
                  above.
                </span>
              </label>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="t-mono-label">ASSISTANT · MEMORY &amp; SKILLS</span>
              <h3 className="t-h3">Memory &amp; skills this assistant sees</h3>
            </div>
            <div className="card-body">
              <AgentMemoryAndSkillsSection agentId={formantChatAgentId} />
            </div>
          </div>
        </div>
      </form>
    </>
  );
}
